import AddIcon from "@mui/icons-material/Add"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import EditOutlinedIcon from "@mui/icons-material/EditOutlined"
import { Box, Chip, IconButton, Stack, Typography } from "@mui/material"
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid"
import { useMemo } from "react"

import Button from "@components/button"
import GridTable from "@components/grid-table"
import { DELETE_COLOR, GRAY_COLOR, GRID_HEADER_ITEM } from "@constants/colors"
import { useChangelogPageContext } from "@contexts/main/changelog_page_context"
import type { ChangelogCategory } from "@models/changelog"

export default function CategoriesSection() {
  const {
    translate,
    categories,
    categoriesLoading,
    announcements,
    openCreateCategory,
    openEditCategory,
    openDeleteCategory
  } = useChangelogPageContext()

  const countByCategory = useMemo(() => {
    const by: Record<string, number> = {}
    announcements.forEach((a) => {
      by[a.categoryId] = (by[a.categoryId] || 0) + 1
    })
    return by
  }, [announcements])

  const columns: GridColDef[] = [
    {
      field: "category",
      flex: 2,
      minWidth: 180,
      headerName: translate("changelog_col_category"),
      renderHeader: () => (
        <Box sx={{ color: GRID_HEADER_ITEM }}>
          {translate("changelog_col_category")}
        </Box>
      ),
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as ChangelogCategory
        return (
          <Stack direction="row" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                background: row.color
              }}
            />
            <Typography sx={{ fontWeight: 600, fontSize: "0.92rem" }}>
              {row.translations?.tr?.name || "—"}
            </Typography>
          </Stack>
        )
      },
      sortable: false,
      disableColumnMenu: true
    },
    {
      field: "english",
      flex: 2,
      minWidth: 160,
      headerName: translate("changelog_col_english"),
      renderHeader: () => (
        <Box sx={{ color: GRID_HEADER_ITEM }}>
          {translate("changelog_col_english")}
        </Box>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ color: "text.secondary" }}>
          {(params.row as ChangelogCategory).translations?.en?.name || "—"}
        </Box>
      ),
      sortable: false,
      disableColumnMenu: true
    },
    {
      field: "count",
      flex: 1,
      minWidth: 120,
      headerName: translate("changelog_announcement_lower"),
      renderHeader: () => (
        <Box sx={{ color: GRID_HEADER_ITEM }}>
          {translate("changelog_announcement_lower")}
        </Box>
      ),
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={countByCategory[(params.row as ChangelogCategory)._id] || 0}
          size="small"
          sx={{
            height: "1.4rem",
            fontSize: "0.75rem",
            fontWeight: 600,
            background: "#EEF0F2",
            color: "text.secondary"
          }}
        />
      ),
      sortable: false,
      disableColumnMenu: true
    },
    {
      field: "actions",
      minWidth: 100,
      flex: 0.5,
      align: "right",
      headerAlign: "right",
      headerName: "",
      renderHeader: () => <Box />,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as ChangelogCategory
        return (
          <Stack direction="row" gap={0.5}>
            <IconButton
              size="small"
              sx={{ color: GRAY_COLOR }}
              onClick={() => openEditCategory(row)}
            >
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              sx={{ color: DELETE_COLOR }}
              onClick={() => openDeleteCategory(row)}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
        )
      },
      sortable: false,
      disableColumnMenu: true
    }
  ]

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography sx={{ fontSize: "1.4rem", fontWeight: 700 }}>
            {translate("changelog_categories")}
          </Typography>
          <Typography sx={{ color: "text.secondary", fontSize: "0.85rem" }}>
            {categories.length} {translate("changelog_category_lower")}
          </Typography>
        </Box>
        <Button startIcon={<AddIcon />} onClick={openCreateCategory}>
          {translate("changelog_new_category")}
        </Button>
      </Stack>

      <GridTable
        paginationHidden
        loading={categoriesLoading}
        rows={categories}
        columns={columns}
        rowCount={categories.length}
        paginationMode="client"
        getRowId={(row: ChangelogCategory) => row._id}
        disableColumnSelector={true}
        noRowsTableText="changelog_empty_categories"
      />
    </Box>
  )
}
